'use client';

import { useEffect, useRef, useState, type ReactElement } from 'react';

import { cn } from '@/shared/lib/utils';
import type { Subscription } from '@/shared/models/subscription';
import type { Pricing as PricingType } from '@/shared/types/blocks/pricing';

type PricingProps = {
  section: PricingType;
  className?: string;
  currentSubscription?: Subscription;
};

type LoadedPricing = (props: PricingProps) => ReactElement | null;

function PricingSkeleton({
  section,
  className,
}: {
  section: PricingType;
  className?: string;
}) {
  const cardCount = Math.min(
    Math.max(section.items?.filter((item) => item.is_featured).length || 3, 3),
    3
  );

  return (
    <section
      id={section.id}
      className={cn(
        'from-accent/10 to-background bg-gradient-to-b py-24 md:py-36',
        section.className,
        className
      )}
    >
      <div className="mx-auto mb-12 px-4 text-center md:px-8">
        {section.sr_only_title && (
          <h1 className="sr-only">{section.sr_only_title}</h1>
        )}
        {section.label && (
          <span className="text-primary text-sm font-bold tracking-wider uppercase">
            {section.label}
          </span>
        )}
        <h2 className="mt-4 mb-6 text-3xl font-bold text-pretty lg:text-5xl">
          {section.title}
        </h2>
        {section.description && (
          <p className="text-muted-foreground mx-auto mb-4 max-w-xl lg:max-w-none lg:text-lg">
            {section.description}
          </p>
        )}
      </div>

      <div className="container">
        <div className="mx-auto grid w-full gap-6 md:grid-cols-3">
          {Array.from({ length: cardCount }).map((_, index) => (
            <div
              key={index}
              className="border-border bg-card min-h-[34rem] animate-pulse rounded-lg border p-6"
            >
              <div className="bg-muted mx-auto mb-6 h-10 w-10 rounded-full" />
              <div className="bg-muted mx-auto mb-4 h-8 w-36 rounded-md" />
              <div className="bg-muted mx-auto mb-6 h-12 w-44 rounded-md" />
              <div className="bg-muted mb-6 h-12 rounded-full" />
              <div className="space-y-3">
                {Array.from({ length: 6 }).map((__, itemIndex) => (
                  <div
                    key={itemIndex}
                    className="bg-muted h-5 rounded-md"
                    style={{ width: `${itemIndex % 2 ? 72 : 88}%` }}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function PricingDeferred(props: PricingProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [Pricing, setPricing] = useState<LoadedPricing | null>(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root || shouldLoad) return;

    if (!('IntersectionObserver' in window)) {
      setShouldLoad(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { rootMargin: '600px 0px', threshold: 0.01 }
    );

    observer.observe(root);
    return () => observer.disconnect();
  }, [shouldLoad]);

  useEffect(() => {
    if (!shouldLoad || Pricing) return;

    let cancelled = false;
    void import('@/themes/default/blocks/pricing-client').then((mod) => {
      if (!cancelled) {
        setPricing(() => mod.Pricing);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [Pricing, shouldLoad]);

  return (
    <div ref={rootRef}>
      {Pricing ? <Pricing {...props} /> : <PricingSkeleton {...props} />}
    </div>
  );
}
